import api from 'src/shared/api/axios';

export type WeeklyNarrative = {
    executiveSummary: string;
    riskComment: string;
    nextActions: string[];
};

export type WeeklyNarrativeResponse = {
    narrative: WeeklyNarrative;
    aiWarning?: string | null;
};

function downloadBlob(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}

async function parseBlobApiError(err: any) {
    const blob = err?.response?.data;

    if (!(blob instanceof Blob)) return err;

    try {
        const text = await blob.text();
        const parsed = text ? JSON.parse(text) : null;
        err.response.data = parsed;
        return err;
    } catch {
        return err;
    }
}

export async function downloadWeeklyReportPdf(
    projectId: string,
    params?: { asOf?: string; template?: string; evBasis?: 'CONFIRMED_W2' | 'CONFIRMED_W1' | 'LIVE' },
) {
    const res = await api
        .get(`/projects/${projectId}/reports/weekly.pdf`, {
            params,
            responseType: 'blob',
        })
        .catch(async (err) => {
            throw await parseBlobApiError(err);
        });

    const asOfPart = params?.asOf ? `-${params.asOf}` : '';
    downloadBlob(res.data, `weekly-report-${projectId}${asOfPart}.pdf`);
}

export async function downloadWeeklyReportDocx(
    projectId: string,
    params?: { asOf?: string; template?: string; evBasis?: 'CONFIRMED_W2' | 'CONFIRMED_W1' | 'LIVE' },
) {
    const res = await api
        .get(`/projects/${projectId}/reports/weekly.docx`, {
            params,
            responseType: 'blob',
        })
        .catch(async (err) => {
            throw await parseBlobApiError(err);
        });

    const asOfPart = params?.asOf ? `-${params.asOf}` : '';
    downloadBlob(res.data, `weekly-report-${projectId}${asOfPart}.docx`);
}

export async function fetchWeeklyNarrative(
    projectId: string,
    params?: {
        asOf?: string;
        template?: string;
        regenerate?: boolean;
        evBasis?: 'CONFIRMED_W2' | 'CONFIRMED_W1' | 'LIVE';
    },
): Promise<WeeklyNarrativeResponse> {
    const res = await api.get(`/projects/${projectId}/reports/weekly.narrative`, {
        params: {
            ...params,
            regenerate: params?.regenerate ? '1' : undefined,
        },
    });

    return {
        narrative: (res.data?.narrative ?? res.data) as WeeklyNarrative,
        aiWarning: res.data?.aiWarning ?? null,
    };
}

export async function downloadWeeklyReportPdfWithOverride(
    projectId: string,
    narrativeOverride: WeeklyNarrative,
    params?: { asOf?: string; template?: string; evBasis?: 'CONFIRMED_W2' | 'CONFIRMED_W1' | 'LIVE' },
    templateKey: string = 'default',
) {
    const res = await api
        .post(
            `/projects/${projectId}/reports/weekly.pdf`,
            { templateKey, options: { narrativeOverride } },
            { params, responseType: 'blob' },
        )
        .catch(async (err) => {
            throw await parseBlobApiError(err);
        });

    const asOfPart = params?.asOf ? `-${params.asOf}` : '';
    downloadBlob(res.data, `weekly-report-${projectId}${asOfPart}.pdf`);
}
